// The Marketing command center's view vocabulary — ONE source of truth for
// every surface that names a /paid-ads?view= destination:
//   - PaidAdsPage derives its tab labels (TAB_LABEL) from here
//   - rail-nav derives its ⌘K paletteOnly deep-links (label + keywords) here
// so a new view (or a rename) automatically reaches the tab strip AND the
// palette — the drift where a view existed but was invisible to search can't
// recur. Group membership/order stays in PaidAdsPage (it owns the nav);
// the curated hub rail (hub-nav.ts) stays hand-picked by design.
//
// Retired first-class tabs (decide / search / diagnose / history) stay in
// this manifest so old bookmarks and ⌘K synonyms still resolve. PaidAdsPage
// aliases them onto Performance — the campaign scoreboard + what-shipped
// strip — instead of keeping four overlapping spend views.

export type MarketingViewKey =
  | 'ask'
  | 'decide'
  | 'search'
  | 'performance'
  | 'diagnose'
  | 'conversions'
  | 'history'
  | 'settings'
  | 'channels'
  | 'campaigns'
  | 'openaiads'
  | 'bypage'
  | 'landing'
  | 'content';

export interface MarketingView {
  key: MarketingViewKey;
  /** The in-page tab label; ⌘K shows it as "Marketing · <label>". */
  label: string;
  module: 'paid_ads' | 'acquisition';
  /** Extra ⌘K search terms: retired names, synonyms, card titles. */
  keywords: string[];
}

export const MARKETING_VIEWS: MarketingView[] = [
  { key: 'ask', label: 'Ask', module: 'paid_ads', keywords: ['ai', 'chat', 'assistant'] },
  {
    key: 'performance',
    label: 'Performance',
    module: 'paid_ads',
    keywords: [
      'campaigns',
      'scoreboard',
      'search scorecard',
      'spend',
      'ctr',
      'lp cr',
      'book demo',
      'cpa',
      'what shipped',
      'kpis',
    ],
  },
  {
    key: 'search',
    label: 'Search scorecard',
    module: 'paid_ads',
    keywords: ['performance', 'ctr', 'lp cr', 'book demo', 'onboarding', 'workflow', 'customer', 'targets', 'yesterday'],
  },
  {
    key: 'decide',
    label: 'Scale / Pause',
    module: 'paid_ads',
    keywords: ['spend', 'decide', 'verdicts', 'triage', 'performance'],
  },
  {
    key: 'diagnose',
    label: 'Diagnose',
    module: 'paid_ads',
    keywords: ['wasted spend', 'search terms', 'campaign table', 'ledger', 'performance'],
  },
  { key: 'conversions', label: 'Conversions vs Google', module: 'paid_ads', keywords: ['ledger', 'uploads', 'google ads', 'reconciliation'] },
  {
    key: 'history',
    label: 'History',
    module: 'paid_ads',
    keywords: ['decision log', 'audit', 'what shipped', 'pins', 'performance'],
  },
  { key: 'settings', label: 'Settings', module: 'paid_ads', keywords: ['budget', 'targets', 'orientation'] },
  { key: 'channels', label: 'Sources', module: 'acquisition', keywords: ['channels', 'funnel by source', 'organic', 'referral'] },
  { key: 'campaigns', label: 'Campaigns', module: 'acquisition', keywords: ['prompt fills', 'paid search', 'utm', 'volume'] },
  { key: 'openaiads', label: 'OpenAI Ads', module: 'acquisition', keywords: ['chatgpt', 'oppref', 'capi', 'conversion uploads', 'clicks', 'openai'] },
  { key: 'bypage', label: 'Conversion pages', module: 'acquisition', keywords: ['by page', 'last touch', 'per-page funnel'] },
  { key: 'landing', label: 'Landing pages', module: 'acquisition', keywords: ['first touch', 'entry pages', 'flows'] },
  { key: 'content', label: 'Content', module: 'acquisition', keywords: ['blog', 'posts', 'articles'] },
];

export const MARKETING_VIEW_LABEL = Object.fromEntries(
  MARKETING_VIEWS.map((v) => [v.key, v.label]),
) as Record<MarketingViewKey, string>;

/** Old spend tabs collapse onto the Performance scoreboard. */
export const MARKETING_VIEW_ALIASES: Partial<Record<MarketingViewKey, MarketingViewKey>> = {
  decide: 'performance',
  search: 'performance',
  diagnose: 'performance',
  history: 'performance',
};

export function resolveMarketingView(raw: string | null, fallback: MarketingViewKey): MarketingViewKey {
  if (!raw || !(raw in MARKETING_VIEW_LABEL)) return fallback;
  const key = raw as MarketingViewKey;
  return MARKETING_VIEW_ALIASES[key] ?? key;
}
